import { useEffect, useMemo, useState } from "react";

import {
  AuthSheet,
  type AuthSheetMode,
  type LoginAccountMode,
  type LoginValues,
  type RegisterValues,
} from "../../../component-library/auth-sheet/AuthSheet.js";
import { loginWithPassword, registerWithEmail, requestEmailCode } from "@/shared/api/session.js";
import { Sheet } from "@/shared/ui/Sheet/index.js";
import { useAuthStore } from "@/shared/stores/authStore.js";

import { useAuthSheetStore } from "./authSheetStore.js";

const CAMPUS_DOMAINS = [
  { id: "mails.ccnu.edu.cn", label: "mails.ccnu.edu.cn" },
  { id: "ccnu.edu.cn", label: "ccnu.edu.cn" },
];
const DEFAULT_CAMPUS_DOMAIN = "mails.ccnu.edu.cn";

const initialRegisterValues: RegisterValues = {
  emailLocal: "",
  code: "",
  username: "",
  displayName: "",
  password: "",
};

const initialLoginValues: LoginValues = {
  emailLocal: "",
  username: "",
  password: "",
};

export function AuthSheetHost() {
  const isOpen = useAuthSheetStore((state) => state.isOpen);
  const mode = useAuthSheetStore((state) => state.mode);
  const closeAuthSheet = useAuthSheetStore((state) => state.closeAuthSheet);
  const setAuthSheetMode = useAuthSheetStore((state) => state.setAuthSheetMode);
  const setSession = useAuthStore((state) => state.setSession);

  const [registerDomain, setRegisterDomain] = useState(DEFAULT_CAMPUS_DOMAIN);
  const [loginDomain, setLoginDomain] = useState(DEFAULT_CAMPUS_DOMAIN);
  const [loginAccountMode, setLoginAccountMode] = useState<LoginAccountMode>("email");
  const [registerValues, setRegisterValues] = useState<RegisterValues>(initialRegisterValues);
  const [loginValues, setLoginValues] = useState<LoginValues>(initialLoginValues);
  const [message, setMessage] = useState("");
  const [messageTone, setMessageTone] = useState<"neutral" | "error" | "success">("neutral");
  const [submitting, setSubmitting] = useState(false);
  const [codeCooldownSeconds, setCodeCooldownSeconds] = useState(0);

  const registerEmail = useMemo(
    () => buildCampusEmail(registerValues.emailLocal, registerDomain),
    [registerDomain, registerValues.emailLocal],
  );
  const loginEmail = useMemo(
    () => buildCampusEmail(loginValues.emailLocal, loginDomain),
    [loginDomain, loginValues.emailLocal],
  );

  // Audit B-1: Escape-to-close is centralized in shared/ui/Sheet/Sheet.tsx
  // via the closeOnEscape default. AuthSheetHost previously installed its
  // own window keydown listener; removed to avoid double-handling and
  // align all sheet hosts on a single keyboard contract.

  useEffect(() => {
    if (codeCooldownSeconds <= 0) return undefined;
    const id = window.setInterval(() => {
      setCodeCooldownSeconds((current) => Math.max(0, current - 1));
    }, 1000);
    return () => window.clearInterval(id);
  }, [codeCooldownSeconds]);

  useEffect(() => {
    if (isOpen) setFormMessage("", "neutral");
  }, [isOpen, mode]);

  if (!isOpen) return null;

  return (
    <Sheet
      open={isOpen}
      onClose={closeAuthSheet}
      overlayKind="auth"
      panelClass="auth-panel"
      labelledBy="auth-sheet-title"
    >
      <AuthSheet
          mode={mode}
          loginAccountMode={loginAccountMode}
          domains={CAMPUS_DOMAINS}
          registerDomain={registerDomain}
          loginDomain={loginDomain}
          registerValues={registerValues}
          loginValues={loginValues}
          message={message}
          messageTone={messageTone}
          codeCooldownSeconds={codeCooldownSeconds}
          submitting={submitting}
          onClose={closeAuthSheet}
          onModeChange={handleModeChange}
          onLoginAccountModeChange={setLoginAccountMode}
          onRegisterDomainChange={setRegisterDomain}
          onLoginDomainChange={setLoginDomain}
          onRegisterChange={handleRegisterChange}
          onLoginChange={handleLoginChange}
          onRequestCode={() => void handleRequestCode()}
          onSubmitRegister={() => void handleRegisterSubmit()}
        onSubmitLogin={() => void handleLoginSubmit()}
      />
    </Sheet>
  );

  function handleModeChange(nextMode: AuthSheetMode) {
    setAuthSheetMode(nextMode);
    setFormMessage("", "neutral");
  }

  function handleRegisterChange(field: keyof RegisterValues, value: string) {
    setRegisterValues((current) => ({
      ...current,
      [field]:
        field === "emailLocal"
          ? cleanEmailLocalValue(value)
          : field === "username"
            ? cleanUsernameValue(value)
            : value,
    }));
  }

  function handleLoginChange(field: keyof LoginValues, value: string) {
    setLoginValues((current) => ({
      ...current,
      [field]:
        field === "emailLocal"
          ? cleanEmailLocalValue(value)
          : field === "username"
            ? cleanUsernameValue(value)
            : value,
    }));
  }

  async function handleRequestCode() {
    if (!isCampusEmail(registerEmail)) {
      setFormMessage("先填写 @ 前面的邮箱名。", "error");
      return;
    }

    setSubmitting(true);
    setFormMessage("正在发送验证码...", "neutral");
    try {
      const payload = await requestEmailCode({ email: registerEmail, purpose: "register" });
      setCodeCooldownSeconds(payload.retryAfterSeconds || 60);
      setFormMessage(
        `验证码已发送到 ${payload.email}，${Math.round(payload.expiresInSeconds / 60)} 分钟内有效。`,
        "success",
      );
    } catch (error) {
      setFormMessage(errorMessage(error, "验证码发送失败"), "error");
    } finally {
      setSubmitting(false);
    }
  }

  async function handleRegisterSubmit() {
    const username = cleanUsernameValue(registerValues.username);
    const displayName = registerValues.displayName.trim() || username;

    if (!isCampusEmail(registerEmail)) {
      setFormMessage("请填写华师邮箱。", "error");
      return;
    }
    if (!/^\d{6}$/.test(registerValues.code.trim())) {
      setFormMessage("请填写 6 位验证码。", "error");
      return;
    }
    if (!username) {
      setFormMessage("请填写用户名。", "error");
      return;
    }
    if (registerValues.password.length < 6) {
      setFormMessage("密码至少 6 位。", "error");
      return;
    }

    setSubmitting(true);
    setFormMessage("正在注册，成功后会自动进入...", "neutral");
    try {
      const session = await registerWithEmail({
        username,
        displayName,
        email: registerEmail,
        verificationCode: registerValues.code.trim(),
        password: registerValues.password,
      });
      setSession(session);
      resetForms();
      closeAuthSheet();
    } catch (error) {
      setFormMessage(errorMessage(error, "注册失败"), "error");
    } finally {
      setSubmitting(false);
    }
  }

  async function handleLoginSubmit() {
    const username =
      loginAccountMode === "email" ? loginEmail : cleanUsernameValue(loginValues.username);

    if (loginAccountMode === "email" && !isCampusEmail(username)) {
      setFormMessage("请填写完整的华师邮箱前缀。", "error");
      return;
    }
    if (!username) {
      setFormMessage("请填写账号。", "error");
      return;
    }
    if (!loginValues.password) {
      setFormMessage("请填写密码。", "error");
      return;
    }

    setSubmitting(true);
    setFormMessage("正在登录...", "neutral");
    try {
      const session = await loginWithPassword({
        username,
        password: loginValues.password,
      });
      setSession(session);
      resetForms();
      closeAuthSheet();
    } catch (error) {
      setFormMessage(errorMessage(error, "登录失败"), "error");
    } finally {
      setSubmitting(false);
    }
  }

  function resetForms() {
    setRegisterValues(initialRegisterValues);
    setLoginValues(initialLoginValues);
    setCodeCooldownSeconds(0);
    setFormMessage("", "neutral");
  }

  function setFormMessage(text: string, tone: "neutral" | "error" | "success") {
    setMessage(text);
    setMessageTone(tone);
  }
}

function buildCampusEmail(localPart: string, domain: string) {
  const local = cleanEmailLocalValue(localPart).toLowerCase();
  return local ? `${local}@${domain}` : "";
}

function cleanEmailLocalValue(value: string) {
  return value.replace(/\s/g, "").replace(/^@+/, "").split("@")[0] ?? "";
}

function cleanUsernameValue(value: string) {
  return value.replace(/\s/g, "").replace(/^@+/, "").toLowerCase();
}

function isCampusEmail(value: string) {
  const email = value.trim().toLowerCase();
  return (
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) &&
    (email.endsWith("@mails.ccnu.edu.cn") || email.endsWith("@ccnu.edu.cn"))
  );
}

function errorMessage(error: unknown, fallback: string) {
  return error instanceof Error && error.message ? error.message : fallback;
}
